import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useEmail } from '../../contexts/EmailContext';
import { useContacts } from '../../contexts/ContactContext';
import toast from 'react-hot-toast';
import SafeIcon from '../../common/SafeIcon';
import { FiArrowLeft, FiArrowRight, FiCalendar, FiCheck, FiCopy, FiEdit, FiEye, FiMail, FiPlus, FiSave, FiSend, FiSettings, FiTrash2, FiUsers, FiX } from 'react-icons/fi';


const EmailCampaignBuilder = ({ campaign, onClose }) => {
  const { createCampaign, updateCampaign, getEmailTemplates } = useEmail();
  const { contacts } = useContacts();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'single', // single, sequence, drip
    subject: '',
    content: '',
    templateId: '',
    recipients: [],
    scheduledAt: '',
    abTest: {
      enabled: false,
      subjectB: '',
      splitPercentage: 50
    },
    sequence: [
      {
        id: 1,
        subject: '',
        content: '',
        delay: 0, // days
        delayUnit: 'days'
      }
    ],
    settings: {
      trackOpens: true,
      trackClicks: true,
      unsubscribeLink: true,
      replyTo: 'noreply@prosperitybuilders.com'
    }
  });

  const templates = getEmailTemplates();

  useEffect(() => {
    if (campaign) {
      setFormData({
        name: campaign.name || '',
        description: campaign.description || '',
        type: campaign.type || 'single',
        subject: campaign.subject || '',
        content: campaign.content || '',
        templateId: campaign.templateId || '',
        recipients: campaign.recipients || [],
        scheduledAt: campaign.scheduledAt || '',
        abTest: campaign.abTest || {
          enabled: false,
          subjectB: '',
          splitPercentage: 50
        },
        sequence: campaign.sequence || [
          {
            id: 1,
            subject: '',
            content: '',
            delay: 0,
            delayUnit: 'days'
          }
        ],
        settings: campaign.settings || {
          trackOpens: true,
          trackClicks: true,
          unsubscribeLink: true,
          replyTo: 'noreply@prosperitybuilders.com'
        }
      });
    }
  }, [campaign]);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.subject) {
      toast.error('Campaign name and subject are required');
      return;
    }

    if (formData.recipients.length === 0) {
      toast.error('Please select at least one recipient');
      return;
    }

    if (campaign) {
      updateCampaign(campaign.id, formData);
      toast.success('Campaign updated successfully');
    } else {
      createCampaign(formData);
      toast.success('Campaign created successfully');
    }

    onClose();
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSettingsChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      settings: {
        ...prev.settings,
        [name]: type === 'checkbox' ? checked : value
      }
    }));
  };

  const handleABTestChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      abTest: {
        ...prev.abTest,
        [name]: type === 'checkbox' ? checked : value
      }
    }));
  };

  const handleRecipientToggle = (contactId) => {
    setFormData(prev => ({
      ...prev,
      recipients: prev.recipients.includes(contactId)
        ? prev.recipients.filter(id => id !== contactId)
        : [...prev.recipients, contactId]
    }));
  };

  const selectAllRecipients = () => {
    setFormData(prev => ({
      ...prev,
      recipients: contacts.map(c => c.id)
    }));
  };

  const clearAllRecipients = () => {
    setFormData(prev => ({
      ...prev,
      recipients: []
    }));
  };

  const addSequenceEmail = () => {
    const newEmail = {
      id: formData.sequence.length + 1,
      subject: '',
      content: '',
      delay: 1,
      delayUnit: 'days'
    };
    setFormData(prev => ({
      ...prev,
      sequence: [...prev.sequence, newEmail]
    }));
  };

  const removeSequenceEmail = (id) => {
    setFormData(prev => ({
      ...prev,
      sequence: prev.sequence.filter(email => email.id !== id)
    }));
  };

  const updateSequenceEmail = (id, updates) => {
    setFormData(prev => ({
      ...prev,
      sequence: prev.sequence.map(email => 
        email.id === id ? { ...email, ...updates } : email
      )
    }));
  };

  const applyTemplate = (template) => {
    setFormData(prev => ({
      ...prev,
      subject: template.subject,
      content: template.content,
      templateId: template.id
    }));
    toast.success('Template applied successfully');
  };

  const steps = [
    { id: 1, title: 'Campaign Details', description: 'Basic information and type' },
    { id: 2, title: 'Email Content', description: 'Subject, content, and templates' },
    { id: 3, title: 'Recipients', description: 'Select target audience' },
    { id: 4, title: 'Settings', description: 'Scheduling and options' },
    { id: 5, title: 'Review', description: 'Final review and send' }
  ];

  const renderStepIndicator = () => (
    <div className="px-6 py-4 border-b border-gray-200">
      <div className="flex items-center justify-between">
        {steps.map((stepItem, index) => (
          <div key={stepItem.id} className="flex items-center">
            <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${
              step >= stepItem.id 
                ? 'bg-blue-600 text-white' 
                : 'bg-gray-200 text-gray-600'
            }`}>
              {step > stepItem.id ? <SafeIcon icon={FiCheck} className="text-sm" /> : stepItem.id}
            </div>
            <div className="ml-3">
              <p className={`text-sm font-medium ${step >= stepItem.id ? 'text-blue-600' : 'text-gray-500'}`}>
                {stepItem.title}
              </p>
              <p className="text-xs text-gray-500">{stepItem.description}</p>
            </div>
            {index < steps.length - 1 && (
              <div className={`w-12 h-0.5 mx-4 ${step > stepItem.id ? 'bg-blue-600' : 'bg-gray-200'}`} />
            )}
          </div>
        ))}
      </div>
    </div>
  );

  const renderCampaignDetails = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Campaign Name *
          </label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="e.g., Q1 2024 Agent Outreach"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Campaign Type
          </label>
          <select
            name="type"
            value={formData.type}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="single">Single Email</option>
            <option value="sequence">Email Sequence</option>
            <option value="drip">Drip Campaign</option>
          </select>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Description
        </label>
        <textarea
          name="description"
          value={formData.description}
          onChange={handleChange}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="Brief description of the campaign purpose and goals"
        />
      </div>

      {formData.type === 'sequence' && (
        <div className="bg-blue-50 rounded-lg p-4">
          <h4 className="font-medium text-blue-900 mb-2">Email Sequence</h4>
          <p className="text-sm text-blue-700">
            Create a series of emails that will be sent automatically at specified intervals.
            Perfect for nurturing prospects through the recruitment funnel.
          </p>
        </div>
      )}

      {formData.type === 'drip' && (
        <div className="bg-purple-50 rounded-lg p-4">
          <h4 className="font-medium text-purple-900 mb-2">Drip Campaign</h4>
          <p className="text-sm text-purple-700">
            Automated emails triggered by specific actions or time intervals.
            Ideal for long-term prospect nurturing and relationship building.
          </p>
        </div>
      )}
    </div>
  );

  const renderEmailContent = () => (
    <div className="space-y-6">
      {/* Template Selection */}
      <div>
        <h4 className="font-medium text-gray-900 mb-3">Choose Template (Optional)</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {templates.slice(0, 4).map(template => (
            <div
              key={template.id}
              className="p-4 border border-gray-200 rounded-lg hover:border-blue-300 cursor-pointer transition-colors"
              onClick={() => applyTemplate(template)}
            >
              <div className="flex items-start justify-between mb-2">
                <h5 className="font-medium text-gray-900">{template.name}</h5>
                <SafeIcon icon={FiCopy} className="text-gray-400 hover:text-blue-600" />
              </div>
              <p className="text-sm text-gray-600 mb-2">{template.description}</p>
              <p className="text-xs text-gray-500">Category: {template.category}</p>
            </div>
          ))}
        </div>
      </div>

      {formData.type === 'single' ? (
        // Single Email Content
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Subject Line *
            </label>
            <input
              type="text"
              name="subject"
              value={formData.subject}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter compelling subject line"
            />
          </div>

          {/* A/B Test Toggle */}
          <div className="flex items-center">
            <input
              type="checkbox"
              id="abTestEnabled"
              name="enabled"
              checked={formData.abTest.enabled}
              onChange={handleABTestChange}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <label htmlFor="abTestEnabled" className="ml-2 text-sm text-gray-700">
              Enable A/B testing for subject line
            </label>
          </div>

          {formData.abTest.enabled && (
            <div className="bg-yellow-50 rounded-lg p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Subject Line B
                  </label>
                  <input
                    type="text"
                    name="subjectB"
                    value={formData.abTest.subjectB}
                    onChange={handleABTestChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Alternative subject line"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Split Percentage
                  </label>
                  <select
                    name="splitPercentage"
                    value={formData.abTest.splitPercentage}
                    onChange={handleABTestChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value={25}>25% / 75%</option>
                    <option value={50}>50% / 50%</option>
                    <option value={75}>75% / 25%</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email Content *
            </label>
            <textarea
              name="content"
              value={formData.content}
              onChange={handleChange}
              rows={12}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Write your email content here..."
            />
          </div>
        </div>
      ) : (
        // Email Sequence Content
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h4 className="font-medium text-gray-900">Email Sequence</h4>
            <button
              type="button"
              onClick={addSequenceEmail}
              className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
            >
              <SafeIcon icon={FiPlus} className="text-sm" />
              Add Email
            </button>
          </div>

          {formData.sequence.map((email, index) => (
            <div key={email.id} className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-4">
                <h5 className="font-medium text-gray-900">Email {index + 1}</h5>
                {formData.sequence.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeSequenceEmail(email.id)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <SafeIcon icon={FiTrash2} className="text-sm" />
                  </button>
                )}
              </div>

              {index > 0 && (
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Send After
                    </label>
                    <input
                      type="number"
                      value={email.delay}
                      onChange={(e) => updateSequenceEmail(email.id, { delay: parseInt(e.target.value) })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      min="1"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Time Unit
                    </label>
                    <select
                      value={email.delayUnit}
                      onChange={(e) => updateSequenceEmail(email.id, { delayUnit: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="hours">Hours</option>
                      <option value="days">Days</option>
                      <option value="weeks">Weeks</option>
                    </select>
                  </div>
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Subject Line
                  </label>
                  <input
                    type="text"
                    value={email.subject}
                    onChange={(e) => updateSequenceEmail(email.id, { subject: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter subject line"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Content
                  </label>
                  <textarea
                    value={email.content}
                    onChange={(e) => updateSequenceEmail(email.id, { content: e.target.value })}
                    rows={6}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Write email content..."
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Variables Help */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="font-medium text-gray-900 mb-2">Personalization Variables</h4>
        <p className="text-sm text-gray-600 mb-3">
          Use these variables to personalize your emails:
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {[
            '{{firstName}}',
            '{{lastName}}',
            '{{company}}',
            '{{title}}',
            '{{experience}}',
            '{{location}}',
            '{{currentProduction}}',
            '{{leadSource}}'
          ].map(variable => (
            <span
              key={variable}
              className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded font-mono cursor-pointer hover:bg-blue-200"
              onClick={() => {
                navigator.clipboard.writeText(variable);
                toast.success('Variable copied to clipboard');
              }}
            >
              {variable}
            </span>
          ))}
        </div>
      </div>
    </div>
  );

  const renderRecipients = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900">Select Recipients</h3>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={selectAllRecipients}
            className="text-sm text-blue-600 hover:text-blue-700"
          >
            Select All
          </button>
          <button
            type="button"
            onClick={clearAllRecipients}
            className="text-sm text-gray-600 hover:text-gray-700"
          >
            Clear All
          </button>
        </div>
      </div>

      <div className="bg-gray-50 rounded-lg p-4">
        <p className="text-sm text-gray-600">
          Selected: <span className="font-medium">{formData.recipients.length}</span> contacts
        </p>
      </div>

      <div className="max-h-96 overflow-y-auto border border-gray-200 rounded-lg">
        {contacts.map(contact => (
          <div
            key={contact.id}
            className="flex items-center p-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
          >
            <input
              type="checkbox"
              checked={formData.recipients.includes(contact.id)}
              onChange={() => handleRecipientToggle(contact.id)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-3"
            />
            <div className="flex-1">
              <div className="text-sm font-medium text-gray-900">
                {contact.firstName} {contact.lastName}
              </div>
              <div className="text-sm text-gray-500">{contact.email}</div>
            </div>
            <div className="text-sm text-gray-500">{contact.company}</div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderSettings = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Campaign Settings</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Schedule Send (Optional)
            </label>
            <input
              type="datetime-local"
              name="scheduledAt"
              value={formData.scheduledAt}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Reply To Email
            </label>
            <input
              type="email"
              name="replyTo"
              value={formData.settings.replyTo}
              onChange={handleSettingsChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        <div className="mt-6">
          <h4 className="font-medium text-gray-900 mb-3">Tracking Options</h4>
          <div className="space-y-3">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="trackOpens"
                name="trackOpens"
                checked={formData.settings.trackOpens}
                onChange={handleSettingsChange}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <label htmlFor="trackOpens" className="ml-2 text-sm text-gray-700">
                Track email opens
              </label>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="trackClicks"
                name="trackClicks"
                checked={formData.settings.trackClicks}
                onChange={handleSettingsChange}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <label htmlFor="trackClicks" className="ml-2 text-sm text-gray-700">
                Track link clicks
              </label>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="unsubscribeLink"
                name="unsubscribeLink"
                checked={formData.settings.unsubscribeLink}
                onChange={handleSettingsChange}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <label htmlFor="unsubscribeLink" className="ml-2 text-sm text-gray-700">
                Include unsubscribe link
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderReview = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-medium text-gray-900">Campaign Review</h3>

      <div className="bg-gray-50 rounded-lg p-6 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <span className="text-sm text-gray-500">Campaign Name:</span>
            <p className="font-medium">{formData.name}</p>
          </div>
          <div>
            <span className="text-sm text-gray-500">Type:</span>
            <p className="font-medium capitalize">{formData.type}</p>
          </div>
          <div>
            <span className="text-sm text-gray-500">Recipients:</span>
            <p className="font-medium">{formData.recipients.length} contacts</p>
          </div>
          <div>
            <span className="text-sm text-gray-500">Schedule:</span>
            <p className="font-medium">
              {formData.scheduledAt ? new Date(formData.scheduledAt).toLocaleString() : 'Send immediately'}
            </p>
          </div>
        </div>

        {formData.type === 'single' && (
          <div>
            <span className="text-sm text-gray-500">Subject Line:</span>
            <p className="font-medium">{formData.subject}</p>
            {formData.abTest.enabled && (
              <p className="text-sm text-blue-600">A/B Test: {formData.abTest.subjectB}</p>
            )}
          </div>
        )}

        {formData.type !== 'single' && (
          <div>
            <span className="text-sm text-gray-500">Email Sequence:</span>
            <p className="font-medium">{formData.sequence.length} emails</p>
          </div>
        )}
      </div>

      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex items-start">
          <SafeIcon icon={FiMail} className="text-yellow-600 mt-0.5 mr-2" />
          <div>
            <h4 className="font-medium text-yellow-900">Ready to Send</h4>
            <p className="text-sm text-yellow-700 mt-1">
              Your campaign is ready to be sent to {formData.recipients.length} recipients.
              {formData.scheduledAt ? ' It will be sent at the scheduled time.' : ' It will be sent immediately after confirmation.'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white rounded-xl shadow-xl w-full max-w-6xl max-h-[95vh] overflow-hidden flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {campaign ? 'Edit Email Campaign' : 'Create Email Campaign'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <SafeIcon icon={FiX} className="text-gray-500" />
          </button>
        </div>

        {/* Step Indicator */}
        {renderStepIndicator()}

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          <form onSubmit={handleSubmit} className="p-6">
            <AnimatePresence mode="wait">
              <motion.div
                key={step}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
              >
                {step === 1 && renderCampaignDetails()}
                {step === 2 && renderEmailContent()}
                {step === 3 && renderRecipients()}
                {step === 4 && renderSettings()}
                {step === 5 && renderReview()}
              </motion.div>
            </AnimatePresence>
          </form>
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center p-6 border-t border-gray-200">
          <div>
            {step > 1 && (
              <button
                type="button"
                onClick={() => setStep(step - 1)}
                className="flex items-center gap-2 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                <SafeIcon icon={FiArrowLeft} className="text-sm" />
                Previous
              </button>
            )}
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              Cancel
            </button>

            {step < 5 ? (
              <button
                type="button"
                onClick={() => setStep(step + 1)}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Next
                <SafeIcon icon={FiArrowRight} className="text-sm" />
              </button>
            ) : (
              <button
                type="submit"
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <SafeIcon icon={formData.scheduledAt ? FiCalendar : FiSend} className="text-sm" />
                {formData.scheduledAt ? 'Schedule Campaign' : 'Send Campaign'}
              </button>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default EmailCampaignBuilder;